import { ReactNode } from 'react';
import { Dialog, DialogProps, PaperProps } from '@mui/material';

import { FullScreenDialogProvider, useFullScreenDialog } from './FullScreenDialogContext';

export interface FullScreenDialogProps extends Omit<DialogProps, 'fullScreen' | 'PaperProps'> {
    /**
     * Whether the dialog is open
     */
    open: boolean;

    /**
     * Callback fired when the dialog should be closed
     * @param event - The event source of the callback
     * @param reason - The reason for closing: 'backdropClick', 'escapeKeyDown', or 'programmatic'
     */
    onClose?: (event: object, reason: 'backdropClick' | 'escapeKeyDown' | 'programmatic') => void;

    /**
     * The content of the dialog
     */
    children: ReactNode;

    /**
     * Paper variant to control the background surface
     * - 'default': Uses the default white surface
     * - 'cement': Uses the cement-50 surface from the theme
     * @default 'default'
     */
    variant?: 'default' | 'cement';

    /**
     * Scroll behavior for the dialog
     * Passes through directly to the Dialog scroll prop
     * @default 'paper'
     */
    scroll?: DialogProps['scroll'];

    /**
     * Additional props for the Paper component
     */
    paperProps?: Omit<PaperProps, 'ref'>;
    /**
     * Whether the dialog should render as a page rather than a modal
     * Changes the behavior to:
     * - Not use a portal
     * - Not use a backdrop
     * - Not use the modal z-index
     * @default false
     */
    asPage?: boolean;
}

function FullScreenDialogRoot({
    open,
    children,
    variant,
    scroll,
    paperProps,
    asPage = false,
    ...dialogProps
}: Omit<FullScreenDialogProps, 'onClose'>) {
    const { handleClose } = useFullScreenDialog();

    // Determine Paper variant based on variant prop
    const paperVariantValue = variant === 'cement' ? 'filled' : undefined;

    return (
        <Dialog
            fullScreen
            open={asPage ? true : open}
            onClose={handleClose}
            scroll={scroll}
            hideBackdrop={asPage}
            slotProps={{
                root: {
                    disablePortal: asPage,
                    sx: (theme) => ({
                        zIndex: asPage ? 1 : theme.zIndex.modal - 10,
                    }),
                },
                paper: {
                    ...paperProps,
                    variant: paperVariantValue,
                    sx: {
                        ...paperProps?.sx,
                    },
                },
            }}
            aria-hidden="false"
            {...dialogProps}>
            {children}
        </Dialog>
    );
}

function FullScreenDialog({ onClose, ...props }: FullScreenDialogProps) {
    return (
        <FullScreenDialogProvider onClose={onClose}>
            <FullScreenDialogRoot {...props} />
        </FullScreenDialogProvider>
    );
}

export default FullScreenDialog;
